// What a declined vetting request actually said — RON-6002 §4.4.
//
// WHY A POPUP AND NOT THE ROW.
//
// A declined request sits on the list until the nightly sweep clears it, which is
// `purge_never_visited_months` — three months by default. The decline reason is the one thing the
// host needs, and it is also the one thing that goes stale fastest: a sentence about last
// Tuesday's holiday does not deserve the full width of the table for a quarter.
//
// So the row carries the facts that stay useful (who, which company, when it was refused) and the
// reason lives one click away, exactly as a cancelled visit shows its status in the row and its
// detail behind View.
//
// House modal markup (`modal successpopup`, `modal-dialog-centered`, `popup-title`), not
// `InformationPopupModal` — that one takes a plain string and renders a green success tick, which
// is the wrong thing to show somebody who has just been told no.

import { useTranslation } from "react-i18next";

import { Images } from "../../Common/Images.jsx";
import VisitorDetailRow from "./VisitorDetailRow.jsx";
import { formatISOToLong } from "../visitorDateUtils.js";

const VisitorVettingDeclinedModal = ({ request, onClose }) => {
  const { t } = useTranslation("common");

  if (!request) return null;

  const date = (value) => (value ? formatISOToLong(value, t) : "—");

  return (
    <div
      className="modal successpopup successpopup-backdrop show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-label={t("react_visitors_vetting_declined_heading")}
    >
      <div className="modal-dialog modal-dialog-centered modal-custom-width">
        <div className="modal-content modal-radius">
          <div className="modal-body">
            <div className="close-modal" onClick={onClose}>
              <img src={Images.Close} alt={t("close")} />
            </div>

            <div className="pt-2">
              <h5 className="popup-title">
                {t("react_visitors_vetting_declined_heading")}
              </h5>
            </div>

            <div className="visitor-declined-modal">
              {/* THE REASON FIRST. It is why the host opened this, and burying it under four
                  identity rows would make them hunt for it. */}
              <p className="visitor-declined-modal__label">
                {t("react_visitors_vetting_decline_reason")}
              </p>
              <p className="visitor-declined-modal__reason">
                {/* Optional on the admin side: a host can be told no and not told why. Saying so
                    beats a blank space that reads as a rendering fault. */}
                {request.declineReason ||
                  t("react_visitors_vetting_declined_no_reason")}
              </p>

              <VisitorDetailRow
                label={t("react_visitors_field_contact_name")}
                value={request.contactName || "—"}
              />
              <VisitorDetailRow
                label={t("company")}
                value={request.company || "—"}
              />
              {request.trade && (
                <VisitorDetailRow
                  label={t("react_visitors_field_trade")}
                  value={request.trade}
                />
              )}
              <VisitorDetailRow
                label={t("email_address")}
                value={request.email || "—"}
              />
              {request.reason && (
                <VisitorDetailRow
                  label={t("react_visitors_vetting_your_reason")}
                  value={request.reason}
                />
              )}
              {request.neededBy && (
                <VisitorDetailRow
                  label={t("react_visitors_vetting_needed_by")}
                  value={date(request.neededBy)}
                />
              )}
              <VisitorDetailRow
                label={t("react_visitors_vetting_requested_label")}
                value={date(request.requestedAt)}
              />
              <VisitorDetailRow
                label={t("react_visitors_vetting_declined_label")}
                value={date(request.closedAt)}
              />

              {/* WHY THIS ROW EXISTS AT ALL. §4.10 sends no email on a decline, so the list is
                  the only channel. In the popup it is read once; on the list it would have
                  shouted the same sentence for three months. */}
              <p className="visitor-declined-modal__notice">
                {t("react_visitors_vetting_declined_notice")}
              </p>
            </div>

            <button
              className="main-btn outline-green-btn w-full"
              onClick={onClose}
              type="button"
            >
              {t("close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorVettingDeclinedModal;
